import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import { isEmpty } from 'lodash'
import React from 'react'

import { fetchCatalogueBrowse, saveCatalogueItem } from 'api/procurement'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

const PAGE_SIZE = 50

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  stickyHead: {
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 5,
      background: theme.palette.grey[100]
    }
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  spacer: { flexGrow: 1 },
  note: { marginBottom: theme.spacing(2) },
  slCell: { width: 64 },
  nameCell: { maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }
}))

const inr = (n) => (n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`)

// One dialog for both Add and Edit — `item` null means add. Cost is sent as
// typed; the backend coerces and refuses non-numbers, and a same-name or
// same-reference-id twin comes back as a clear error.
const ITEM_FIELDS = [
  { key: 'name', label: 'Item name *' },
  {
    key: 'reference_id',
    label: 'Reference ID',
    help: 'Leave blank and the system gives the next id for the category.'
  },
  { key: 'category', label: 'Category' },
  { key: 'unit', label: 'Unit' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'cost', label: 'Cost ₹' },
  { key: 'reference_link', label: 'Reference link' }
]

const ItemDialog = ({ item, onClose, onSaved }) => {
  const [form, setForm] = React.useState(() =>
    ITEM_FIELDS.reduce(
      (acc, f) => ({ ...acc, [f.key]: (item && item[f.key]) ?? '' }),
      {}
    )
  )
  const [saving, setSaving] = React.useState(false)

  const set = (key) => (e) => {
    const value = e.target.value
    setForm((f) => ({ ...f, [key]: value }))
  }

  const submit = async () => {
    if (!String(form.name).trim()) {
      eventAlert('Item name is required.', constants.ERROR, 'Catalogue')
      return
    }
    setSaving(true)
    try {
      const saved = await saveCatalogueItem(
        item ? { uuid: item.uuid, ...form } : form
      )
      const ref = saved?.reference_id ? ` (${saved.reference_id})` : ''
      eventAlert(
        item ? `Item saved${ref}.` : `Item added to the catalogue${ref}.`,
        constants.SUCCESS,
        'Catalogue'
      )
      onSaved()
      onClose()
    } catch (error) {
      eventAlert(
        error?.message || 'Could not save the item.',
        constants.ERROR,
        'Catalogue'
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {item ? 'Edit catalogue item' : 'Add item to the catalogue'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="textSecondary">
          Centres search this list when ordering Program Material — name, cost
          and link auto-fill on their order line.
        </Typography>
        {ITEM_FIELDS.map((f) => (
          <Box mt={1.5} key={f.key}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={f.label}
              helperText={f.help}
              value={form[f.key]}
              onChange={set(f.key)}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          disabled={saving}
          onClick={submit}>
          {item ? 'Save' : 'Add item'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// HQ catalogue browse: searchable, paginated view of the program material
// catalogue (the list centres search when ordering). HQ adds and edits items
// in place (2026-08-20); the bulk path stays the backend import
// (data/catalogue.json + import_catalogue). `readOnly` = the centre-team view.
const HqCatalogue = ({ readOnly = false }) => {
  const classes = useStyles()
  const [search, setSearch] = React.useState('')
  const [rows, setRows] = React.useState([])
  const [count, setCount] = React.useState(0)
  const [offset, setOffset] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  // {item} for Edit, {item: null} for Add, null = closed.
  const [dialog, setDialog] = React.useState(null)

  const load = React.useCallback(async (nextOffset, q) => {
    setLoading(true)
    try {
      const res = await fetchCatalogueBrowse({
        search: q,
        limit: PAGE_SIZE,
        offset: nextOffset
      })
      setRows(res?.results || [])
      setCount(res?.count || 0)
      setOffset(nextOffset)
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to load catalogue',
        constants.ERROR,
        'Catalogue'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load(0, '')
  }, [load])

  // Debounce search so we don't fire a request per keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => load(0, search), 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line
  }, [search])

  const from = count === 0 ? 0 : offset + 1
  const to = Math.min(offset + PAGE_SIZE, count)

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.toolbar}>
        {/* No page heading — the tab already labels this "Catalogue". */}
        <TextField
          size="small"
          variant="outlined"
          label="Search name / reference"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 260 }}
        />
        <div className={classes.spacer} />
        {!readOnly && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            onClick={() => setDialog({ item: null })}>
            + Add item
          </Button>
        )}
      </div>

      <Typography variant="body2" color="textSecondary" className={classes.note}>
        Reference list centres search when ordering Program Material.
        {readOnly ? ' Read-only here.' : ''} {count} item
        {count === 1 ? '' : 's'}.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Table size="small" className={classes.stickyHead}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.slCell}>Sl No.</TableCell>
                <TableCell className={classes.nameCell}>Name</TableCell>
                <TableCell>Reference ID</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell align="right">Cost</TableCell>
                {!readOnly && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((it, idx) => (
                <TableRow key={it.uuid || it.reference_id || it.name} hover>
                  <TableCell className={classes.slCell}>
                    {offset + idx + 1}
                  </TableCell>
                  <TableCell className={classes.nameCell}>
                    {it.name}
                    {it.reference_link ? (
                      <a
                        href={it.reference_link}
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginLeft: 8, fontSize: 12 }}>
                        view ↗
                      </a>
                    ) : null}
                  </TableCell>
                  <TableCell>{it.reference_id || '—'}</TableCell>
                  <TableCell>{it.category || '—'}</TableCell>
                  <TableCell>{it.unit || '—'}</TableCell>
                  <TableCell>{it.vendor || '—'}</TableCell>
                  <TableCell align="right">{inr(it.cost)}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        onClick={() => setDialog({ item: it })}>
                        Edit
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {isEmpty(rows) && (
                <TableRow>
                  <TableCell colSpan={readOnly ? 7 : 8}>
                    <Typography variant="body2" color="textSecondary">
                      No catalogue items found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Box
            mt={2}
            display="flex"
            alignItems="center"
            style={{ gap: 12 }}>
            <Typography variant="body2" color="textSecondary">
              {from}–{to} of {count}
            </Typography>
            <div className={classes.spacer} />
            <Button
              size="small"
              disabled={offset === 0}
              onClick={() => load(Math.max(offset - PAGE_SIZE, 0), search)}>
              Previous
            </Button>
            <Button
              size="small"
              disabled={to >= count}
              onClick={() => load(offset + PAGE_SIZE, search)}>
              Next
            </Button>
          </Box>
        </>
      )}

      {dialog && (
        <ItemDialog
          item={dialog.item}
          onClose={() => setDialog(null)}
          onSaved={() => load(offset, search)}
        />
      )}
    </Paper>
  )
}



// ---------------------------------------------------------------------------
// Catalogue tab wrapper (2026-07-17): a top-right-extreme toggle switches
// between the FULL centre catalogue and the At Apartment catalogue (the LXD
// kit lists). The toggle portals into the slot beside the HQ tab strip.
import ReactDOM from 'react-dom'

import { Button as SwitchButton, ButtonGroup } from '@material-ui/core'

import ApartmentCatalogueTab from 'app/pages/Apartment/Procurement/CatalogueTab'
import { HeaderSlotContext } from 'app/pages/Apartment/Procurement/roleContext'
import { GamesList as AfterschoolGamesList } from 'app/pages/Procurement/AfterschoolGames'
import HqConsumablesCatalogue from 'app/pages/Procurement/Hq/HqConsumablesCatalogue'

const MODE_KEY = 'procurement.hq.catalogue.mode'

// `readOnly` (centre team, 2026-07-21): view only — no price/item edits, and
// the At Apartment catalogue (LXD-owned) is hidden entirely.
// `centreId` matters only in readOnly mode: a centre on its own consumables
// roster must not be shown the shared list it cannot order from.
const HqCatalogueSwitcher = ({ readOnly = false, centreId = '' }) => {
  const headerSlot = React.useContext(HeaderSlotContext)
  const [mode, setMode] = React.useState(
    () => window.localStorage.getItem(MODE_KEY) || 'full'
  )
  React.useEffect(() => {
    window.localStorage.setItem(MODE_KEY, mode)
  }, [mode])

  // A centre with a stale 'apartment' preference must not land on the hidden
  // tab — fall back to the main catalogue.
  const effMode = readOnly && mode === 'apartment' ? 'full' : mode

  // Two attached buttons: the active view is solid orange, the other is
  // white with orange text (user 2026-07-17).
  const toggle = (
    <ButtonGroup size="small" color="primary">
      <SwitchButton
        variant={effMode === 'full' ? 'contained' : 'outlined'}
        disableElevation
        onClick={() => setMode('full')}>
        Main catalogue
      </SwitchButton>
      {!readOnly && (
        <SwitchButton
          variant={effMode === 'apartment' ? 'contained' : 'outlined'}
          disableElevation
          onClick={() => setMode('apartment')}>
          At Apartment
        </SwitchButton>
      )}
      <SwitchButton
        variant={effMode === 'afterschool' ? 'contained' : 'outlined'}
        disableElevation
        onClick={() => setMode('afterschool')}>
        Afterschool games
      </SwitchButton>
      <SwitchButton
        variant={effMode === 'consumables' ? 'contained' : 'outlined'}
        disableElevation
        onClick={() => setMode('consumables')}>
        Consumables
      </SwitchButton>
    </ButtonGroup>
  )

  return (
    <>
      {headerSlot ? ReactDOM.createPortal(toggle, headerSlot) : toggle}
      {effMode === 'full' ? (
        <HqCatalogue readOnly={readOnly} />
      ) : effMode === 'afterschool' ? (
        <AfterschoolGamesList editable={!readOnly} />
      ) : effMode === 'consumables' ? (
        <HqConsumablesCatalogue readOnly={readOnly} centreId={centreId} />
      ) : (
        <ApartmentCatalogueTab />
      )}
    </>
  )
}

export default HqCatalogueSwitcher
